import $ from "jquery";

/**
 * Gravity Forms funnel events
 *
 * Pushes the client-side half of the form funnel to the dataLayer:
 *
 *   form_view    form scrolled into view (once per rendered form)
 *   form_start   first interaction with a field (once per rendered form)
 *   form_step    a multi-page form rendered a step beyond the first
 *   form_failed  the submission was rejected (validation error on reload)
 *
 * `form_submit` is deliberately NOT pushed here: it is server-confirmed via a one-time
 * token, so bots hitting the thank-you page cannot fake it. See
 * lib/wp/gf-form-submit-tracking.php and assets/js/lib/content/tracking.js.
 *
 * Identity (form_id / form_name / form_type / trip_type) comes from the data-sb-*
 * attributes that lib/wp/gf-datalayer.php puts on every rendered <form>, so this file
 * is form-agnostic: inline forms on a trip page, standalone lead form pages, multi-page
 * forms and several forms on one page all work without special casing.
 *
 * The forms are non-AJAX, so every page of a multi-page form is a full pageload — each
 * step is simply a newly rendered form and needs no extra bookkeeping.
 */

const FORM_SELECTOR = "form[data-sb-form-name]";

// Per-element bookkeeping, so two instances of the same form stay independent.
const viewed = new WeakSet();
const started = new WeakSet();
const stepped = new WeakSet();
const failed = new WeakSet();

const push = (data) => {
    if (typeof window.SbPushToDataLayer === "function") {
        window.SbPushToDataLayer(data);
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
};

/**
 * Read the form identity that PHP wrote onto the form tag.
 */
const getFormContext = (form) => {
    const $form = $(form);
    const context = {
        form_id: parseInt($form.attr("data-sb-form-id"), 10),
        form_name: $form.attr("data-sb-form-name"),
        form_type: $form.attr("data-sb-form-type"),
    };

    const tripType = $form.attr("data-sb-trip-type");
    if (tripType) {
        context.trip_type = tripType;
    }

    return context;
};

/**
 * Current step of a (multi-page) form. GF renders a hidden
 * `gform_source_page_number_<id>` input holding the page currently displayed.
 */
const getStep = (form, context) => {
    const total = parseInt($(form).attr("data-sb-form-pages"), 10) || 1;
    const current =
        parseInt(
            $(form).find(`input[name="gform_source_page_number_${context.form_id}"]`).val(),
            10,
        ) || 1;

    return { step: current, step_total: total };
};

/**
 * The fields GF flagged as invalid. Labels are reported for readability, ids as the
 * stable key: labels are copy and get rewritten, field ids do not.
 */
const getErrorFields = (form) => {
    const ids = [];
    const labels = [];

    $(form)
        .find(".gfield_error")
        .each(function () {
            const id = ($(this).attr("id") || "").split("_").pop();
            const label = $(this)
                .find(".gfield_label")
                .first()
                .text()
                .replace(/\s+/g, " ")
                .replace(/\s*\*\s*$/, "")
                .trim();

            ids.push(id);
            labels.push(label || id);
        });

    return { ids, labels };
};

/**
 * Whether GF re-rendered this form with validation errors.
 */
const hasErrors = (form) => {
    const $wrapper = $(form).closest(".gform_wrapper");

    return (
        $wrapper.find(".gform_submission_error").length > 0 ||
        $wrapper.hasClass("gform_validation_error") ||
        $(form).find(".gfield_error").length > 0
    );
};

/**
 * form_view: push immediately, without waiting for the observer. Used on renders
 * where the form is shown straight away (an error re-render, a later page of a
 * multi-page form), so `form_view` always precedes the `form_failed` / `form_step`
 * event describing that same render.
 */
const emitView = (form, extra = {}) => {
    if (viewed.has(form)) {
        return;
    }

    viewed.add(form);

    const context = getFormContext(form);
    push({ event: "form_view", ...context, ...getStep(form, context), ...extra });
};

/**
 * form_view: fires when the form first enters the viewport.
 */
const observeView = (form) => {
    if (viewed.has(form)) {
        return;
    }

    const context = getFormContext(form);

    if (!("IntersectionObserver" in window)) {
        viewed.add(form);
        push({ event: "form_view", ...context, ...getStep(form, context) });
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting || viewed.has(form)) {
                    return;
                }

                viewed.add(form);
                observer.disconnect();
                push({ event: "form_view", ...context, ...getStep(form, context) });
            });
        },
        { threshold: 0 },
    );

    observer.observe(form);
};

/**
 * form_start: first *human* interaction with any field of this form.
 * Scripted changes are ignored: prefill, select2 and GF's own conditional logic all
 * trigger `change` on load, which would otherwise report a form start nobody made.
 */
const bindStart = (form) => {
    $(form).on("input.sbFunnel change.sbFunnel", ":input", (event) => {
        if (started.has(form) || !event.originalEvent?.isTrusted) {
            return;
        }

        started.add(form);
        $(form).off(".sbFunnel");

        const context = getFormContext(form);
        push({ event: "form_start", ...context, ...getStep(form, context) });
    });
};

/**
 * form_step: a multi-page form rendered a step beyond the first.
 */
const emitStep = (form) => {
    if (stepped.has(form)) {
        return;
    }

    const context = getFormContext(form);
    const step = getStep(form, context);

    if (step.step_total <= 1 || step.step <= 1) {
        return;
    }

    stepped.add(form);
    push({ event: "form_step", ...context, ...step });
};

/**
 * form_failed: the submission was rejected; GF re-rendered the form with errors.
 */
const emitFailed = (form) => {
    if (failed.has(form)) {
        return;
    }

    if (!hasErrors(form)) {
        return;
    }

    failed.add(form);

    const context = getFormContext(form);
    const errors = getErrorFields(form);

    push({
        event: "form_failed",
        ...context,
        ...getStep(form, context),
        error_fields: errors.labels,
        error_field_ids: errors.ids,
    });

    emitValidationMessages(form);
};

/**
 * validation_message: one push per invalid field, with GF's message. The messages are
 * GF's own copy and never repeat what the visitor typed.
 */
const emitValidationMessages = (form) => {
    const errors = getErrorFields(form);

    $(form)
        .find(".gfield_error")
        .each(function (index) {
            const message = $(this)
                .find(".gfield_validation_message, .validation_message")
                .first()
                .text()
                .replace(/\s+/g, " ")
                .trim();

            if (message) {
                push({
                    event: "validation_message",
                    validation_text: message,
                    validation_field: errors.labels[index],
                });
            }
        });
};

/**
 * Wire up every form currently in the DOM. Safe to call repeatedly — the WeakSets and
 * the namespaced handler keep it idempotent per form element.
 */
const init = () => {
    $(FORM_SELECTOR).each(function () {
        const context = getFormContext(this);
        const failedRender = hasErrors(this);
        const laterStep = getStep(this, context).step > 1;

        // On an error re-render or a later page the form is put in front of the visitor
        // right away, so the view is pushed immediately — keeping the order
        // form_view -> form_failed / form_step. `has_errors` marks the retry view so it
        // can be excluded from funnel counts.
        if (failedRender) {
            emitView(this, { has_errors: true });
        } else if (laterStep) {
            emitView(this);
        } else {
            observeView(this);
        }

        if (!started.has(this)) {
            $(this).off(".sbFunnel");
            bindStart(this);
        }

        emitStep(this);
        emitFailed(this);
    });
};

$(init);

// Re-run for AJAX-rendered forms, should a form ever be switched to ajax="true".
$(document).on("gform_post_render gform_page_loaded", init);
